import React from 'react';
import { i18n } from 'config/locales';
import ValidationElement from 'components/Form/ValidationElement';
import { ValidationElementProps } from 'components/Form/ValidationElement/ValidationElement';
import {
  CommonOnUpdate, CommonOnError, CommonScrollIntoView, CommonRequired
} from 'types/props';
import { ErrorCode } from 'redux/modules/application/reducers/error';
import { LegalInvestigationsDeniedItemModel } from 'forms/sections/legal/investigation/issues/types';
import Field from 'components/Form/Field';
import NotApplicable from 'components/Form/NotApplicable';
import { LegalInvestigationsInvestigatingAgencyModel } from 'forms/sections/legal/investigation/history/types';
import DateControl from 'components/Form/DateControl';
import Textarea from 'components/Form/Textarea';
import { LEGAL_INVESTIGATIONS_CLEARANCE_ISSUES } from 'config/formSections/legal';
import { isPVQForm } from 'helpers/form';
import InvestigatingAgency from './InvestigatingAgency';

interface Props extends Partial<LegalInvestigationsDeniedItemModel>, ValidationElementProps {
  onUpdate?: CommonOnUpdate
  onError?: CommonOnError
  required?: CommonRequired
  scrollIntoView?: CommonScrollIntoView
  formType?: string
  prefix: string
}

export default class DeniedItem extends ValidationElement<Props> {
  update = (queue: Partial<LegalInvestigationsDeniedItemModel>) => {
    this.props.onUpdate({
      Agency: this.props.Agency,
      AgencyExplanation: this.props.AgencyExplanation,
      AgencyNotApplicable: this.props.AgencyNotApplicable,
      Date: this.props.Date,
      DateNotApplicable: this.props.DateNotApplicable,
      Explanation: this.props.Explanation,
      ...queue,
    });
  };

  updateDate = (values: LegalInvestigationsDeniedItemModel['Date']) => {
    this.update({
      Date: values,
    });
  };

  updateDateNotApplicable = (
    values: LegalInvestigationsDeniedItemModel['DateNotApplicable']
  ) => {
    this.update({
      DateNotApplicable: values,
      Date: {}
    });
  };

  updateAgency = (values: Partial<LegalInvestigationsDeniedItemModel>) => {
    this.update({
      Agency: values.Agency,
      AgencyExplanation: values.Agency?.value === 'Other' ? this.props.AgencyExplanation : {} as LegalInvestigationsDeniedItemModel['AgencyExplanation'],
    });
  };

  updateAgencyExplanation = (value: LegalInvestigationsDeniedItemModel['AgencyExplanation']) => {
    this.update({
      AgencyExplanation: value
    });
  };

  updateAgencyNotApplicable = (
    values: LegalInvestigationsDeniedItemModel['AgencyNotApplicable']
  ) => {
    this.update({
      AgencyNotApplicable: values,
      Agency: {} as LegalInvestigationsDeniedItemModel['Agency'],
      AgencyExplanation: {} as LegalInvestigationsDeniedItemModel['AgencyExplanation'],
    });
  };

  updateExplanation = (values: LegalInvestigationsDeniedItemModel['Explanation']) => {
    this.update({
      Explanation: values,
    });
  };

  render() {
    const attribute = `DenialList.items[${this.props.$index}].Item`;
    const isPVQ = isPVQForm(this.props.formType);

    return isPVQ ? (
      <div>
        <Field
          title={i18n.t(`${this.props.prefix}.denied.heading.agency`)}
          adjustFor="big-buttons"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Agency`}
          exactAttribute
        >
          <InvestigatingAgency
            {...{
              Agency: this.props.Agency,
              Explanation: this.props.AgencyExplanation,
            }}
            name="Agency"
            onUpdate={this.updateAgency}
            onError={this.props.onError}
            className="legal-investigations-history-agency"
            required={this.props.required && this.props.AgencyNotApplicable?.applicable}
            scrollIntoView={this.props.scrollIntoView}
            attribute={attribute}
            formType={this.props.formType}
          />
        </Field>

        {this.props.Agency?.value === 'Other'
                          && (
                          <Field
                            title={i18n.t('legal.pvq.investigations.history.heading.agencyexplanation')}
                            scrollIntoView={this.props.scrollIntoView}
                            attribute={`${attribute}.AgencyExplanation`}
                            exactAttribute
                          >
                            <Textarea
                              {...this.props.AgencyExplanation}
                              name="AgencyExplanation"
                              className="legal-investigations-suspended-agency-explanation"
                              onUpdate={this.updateAgencyExplanation}
                              onError={this.props.onError}
                              required={this.props.required}
                              disabled={this.props.disabled}
                              attribute={`${this.props.attribute}.AgencyExplanation`}
                            />
                          </Field>
                          )}

        <Field
          title={i18n.t(`${this.props.prefix}.denied.heading.date`)}
          help="legal.pvq.investigations.clearanceissues.denied.help.date"
          adjustFor="datecontrol"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Date`}
        >
          <NotApplicable
            {...this.props.DateNotApplicable}
            name="DateNotApplicable"
            className="legal-investigations-denied-date-notapplicable"
            onUpdate={this.updateDateNotApplicable}
            onError={this.props.onError}
            or={i18n.m(`${this.props.prefix}.denied.para.or`)}
            label={i18n.t(`${this.props.prefix}.denied.label.idk`)}
            required={this.props.required}
            attribute={`${attribute}.DateNotApplicable`}
            includeNAConfirmation={Boolean(this.props.Date)}
          >
            <DateControl
              {...this.props.Date}
              name="Date"
              onUpdate={this.updateDate}
              minDateEqualTo
              onError={this.props.onError}
              className="legal-investigations-denied-date"
              required={this.props.required}
              hideDay
              attribute={`${attribute}.Date`}
            />
          </NotApplicable>
        </Field>

        <Field
          title={i18n.t(`${this.props.prefix}.denied.heading.explanation`)}
          help={`${this.props.prefix}.denied.help.explanation`}
          adjustFor="textarea"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Explanation`}
        >
          <Textarea
            {...this.props.Explanation}
            name="Explanation"
            onUpdate={this.updateExplanation}
            onError={this.props.onError}
            className="legal-investigations-revoked-explanation"
            required={this.props.required}
            attribute={`${attribute}.Explanation`}
          />
        </Field>
      </div>
    ) : (
      <div>
        <Field
          title={i18n.t(`${this.props.prefix}.denied.heading.agency`)}
          adjustFor="big-buttons"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Agency`}
          exactAttribute
        >
          <NotApplicable
            {...this.props.AgencyNotApplicable}
            name="AgencyNotApplicable"
            className="legal-investigations-denied-agency-notapplicable"
            onUpdate={this.updateAgencyNotApplicable}
            onError={this.props.onError}
            or={i18n.m(`${this.props.prefix}.denied.para.or`)}
            label={i18n.t(`${this.props.prefix}.denied.label.idk`)}
            required={this.props.required}
            attribute={`${attribute}.AgencyNotApplicable`}
            includeNAConfirmation={Boolean(this.props.Agency?.value || this.props.AgencyExplanation?.value)}
          >
            <InvestigatingAgency
              {...{
                Agency: this.props.Agency,
                Explanation: this.props.AgencyExplanation,
              }}
              name="Agency"
              onUpdate={this.updateAgency}
              onError={this.props.onError}
              className="legal-investigations-history-agency"
              required={this.props.required && this.props.AgencyNotApplicable?.applicable}
              scrollIntoView={this.props.scrollIntoView}
              attribute={attribute}
              formType={this.props.formType}
            />
          </NotApplicable>
        </Field>

        {this.props.Agency?.value === 'Other'
                          && (
                          <Field
                            title={i18n.t('legal.pvq.investigations.history.heading.agencyexplanation')}
                            scrollIntoView={this.props.scrollIntoView}
                            attribute={`${attribute}.AgencyExplanation`}
                            exactAttribute
                          >
                            <Textarea
                              {...this.props.AgencyExplanation}
                              name="AgencyExplanation"
                              className="legal-investigations-suspended-agency-explanation"
                              onUpdate={this.updateAgencyExplanation}
                              onError={this.props.onError}
                              required={this.props.required}
                              disabled={this.props.disabled}
                              attribute={`${this.props.attribute}.AgencyExplanation`}
                            />
                          </Field>
                          )}

        <Field
          title={i18n.t(`${this.props.prefix}.denied.heading.date`)}
          help="legal.pvq.investigations.clearanceissues.denied.help.date"
          adjustFor="datecontrol"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Date`}
        >
          <NotApplicable
            {...this.props.DateNotApplicable}
            name="DateNotApplicable"
            className="legal-investigations-denied-date-notapplicable"
            onUpdate={this.updateDateNotApplicable}
            onError={this.props.onError}
            or={i18n.m(`${this.props.prefix}.denied.para.or`)}
            label={i18n.t(`${this.props.prefix}.denied.label.idk`)}
            required={this.props.required}
            attribute={`${attribute}.DateNotApplicable`}
            includeNAConfirmation={Boolean(this.props.Date)}
          >
            <DateControl
              {...this.props.Date}
              name="Date"
              onUpdate={this.updateDate}
              minDateEqualTo
              onError={this.props.onError}
              className="legal-investigations-denied-date"
              required={this.props.required}
              hideDay
              attribute={`${attribute}.Date`}
            />
          </NotApplicable>
        </Field>

        <Field
          title={i18n.t(`${this.props.prefix}.denied.heading.explanation`)}
          help={`${this.props.prefix}.denied.help.explanation`}
          adjustFor="textarea"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Explanation`}
        >
          <Textarea
            {...this.props.Explanation}
            name="Explanation"
            onUpdate={this.updateExplanation}
            onError={this.props.onError}
            className="legal-investigations-revoked-explanation"
            required={this.props.required}
            attribute={`${attribute}.Explanation`}
          />
        </Field>
      </div>
    );
  }
}

DeniedItem.defaultProps = {
  onUpdate: (queue) => { },
  onError: (value, arr: ErrorCode[]) => arr,
  Agency: {},
  AgencyExplanation: {},
  AgencyNotApplicable: { applicable: true },
  Date: {},
  DateNotApplicable: { applicable: true },
  Explanation: {},
};
